import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceListingService, cardService } from '../services/api';

interface Card {
  id: string;
  name: string;
  imageUrl?: string;
  cardNumber: string;
  rarity?: string;
  type?: string;
}

interface ListingItem {
  id: string; // unique internal id for list key (uuid or card.id + timestamp)
  card: Card;
  price: string;
  quantity: string;
}

const CreateListingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Card[]>([]);

  // Batch Listing State
  const [listingItems, setListingItems] = useState<ListingItem[]>([]);

  // Global form fields
  const typeFromUrl = searchParams.get('type');
  const cardIdFromUrl = searchParams.get('cardId');
  const [listingType, setListingType] = useState<'WTS' | 'WTB'>(
    typeFromUrl === 'WTB' ? 'WTB' : 'WTS'
  );
  const [externalLink, setExternalLink] = useState('');
  const [description, setDescription] = useState('');

  // Hardcoded defaults
  const shippingCost = '0';
  const condition = 'mint';
  const isBundle = false;
  const [images] = useState<File[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Update listing type when URL parameter changes
  useEffect(() => {
    if (typeFromUrl === 'WTB') {
      setListingType('WTB');
    } else if (typeFromUrl === 'WTS') {
      setListingType('WTS');
    }
  }, [typeFromUrl]);

  // Check user role (Removed - all users can list)
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      navigate('/login');
    }
  }, [navigate]);

  // Track processed card ID to prevent infinite loops or double-fetching
  const processedCardIdRef = useRef<string | null>(null);

  // Auto-add card when cardIdFromUrl changes
  useEffect(() => {
    const fetchCardById = async () => {
      if (!cardIdFromUrl) return;

      // Prevent processing the same ID multiple times
      if (processedCardIdRef.current === cardIdFromUrl) return;

      processedCardIdRef.current = cardIdFromUrl;

      try {
        setLoading(true);
        const response = await cardService.getCard(cardIdFromUrl);
        const cardData = response.data.data || response.data;
        if (cardData) {
          const newCard: Card = {
            id: cardData.id,
            name: cardData.name,
            imageUrl: cardData.imageUrl || cardData.imageBlobPath,
            cardNumber: cardData.cardNumber,
            rarity: cardData.rarity,
            type: cardData.type
          };
          // Using function update to access current state safely without adding dependency
          setListingItems(prev => {
            // Compare IDs as strings to avoid type mismatches
            if (prev.some(item => String(item.card.id) === String(cardData.id))) {
              return prev;
            }
            const newItem: ListingItem = {
              id: `${cardData.id}-${Date.now()}`,
              card: newCard,
              price: '',
              quantity: '1'
            };
            return [...prev, newItem];
          });
          // Clear search results/term like the manual add does, to be clean
          setSearchResults([]);
          setSearchTerm('');
        }
      } catch (error) {
        console.error('Failed to fetch card:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCardById();
  }, [cardIdFromUrl]);

  // Search logic
  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      setLoading(true);
      const response = await cardService.getCards({ search: searchTerm, limit: 10 });
      setSearchResults(response.data.data);
    } catch (error) {
      console.error('Failed to search cards:', error);
      setError(t('createListing.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const addItem = (card: Card) => {
    const newItem: ListingItem = {
      id: `${card.id}-${Date.now()}`, // Unique ID for key prop
      card,
      price: '',
      quantity: '1'
    };
    setListingItems(prev => [...prev, newItem]);
    setSearchResults([]);
    setSearchTerm('');
  };

  const removeItem = (id: string) => {
    setListingItems(prev => prev.filter(item => item.id !== id));
  };

  const updateItem = (id: string, field: 'price' | 'quantity', value: string) => {
    setListingItems(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, [field]: value };
      }
      return item;
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (listingItems.length === 0) {
      setError(t('createListing.errors.selectCard') || 'Please select at least one card.');
      return;
    }

    // Validate inputs
    for (const item of listingItems) {
      if (item.price && parseFloat(item.price) <= 0) {
        setError(t('createListing.errors.invalidPrice'));
        return;
      }
      if (!item.quantity || parseInt(item.quantity) <= 0) {
        setError(t('createListing.errors.invalidQuantity'));
        return;
      }
    }

    try {
      setLoading(true);
      let successCount = 0;
      const failedItems: ListingItem[] = [];
      const failures: { name: string; reason: string }[] = [];

      // Sequential submission to avoid spamming limits or race conditions
      for (const item of listingItems) {
        try {
          const formData = new FormData();
          formData.append('listingType', listingType);
          if (item.price) formData.append('price', item.price);
          if (externalLink) formData.append('externalLink', externalLink.trim());
          if (description.trim()) formData.append('description', description);

          formData.append('shippingCost', shippingCost); // '0'
          formData.append('condition', condition); // 'mint'
          formData.append('quantity', item.quantity);
          formData.append('isBundle', String(isBundle)); // 'false'
          formData.append('cardId', item.card.id);

          // Images (empty)
          images.forEach((image) => {
            formData.append('images', image);
          });

          await marketplaceListingService.createListing(formData);
          successCount++;
        } catch (err: any) {
          console.error(`Failed to create listing for ${item.card.name}:`, err);
          failedItems.push(item);
          failures.push({
            name: item.card.name,
            reason: err.response?.data?.message || err.message || 'Unknown error'
          });
        }
      }

      // Update the list to only show failed items
      setListingItems(failedItems);

      if (failedItems.length === 0) {
        // All successful
        setSuccess(t('createListing.step4.success') || `Successfully created ${successCount} listings!`);
        setTimeout(() => {
          navigate('/marketplace');
        }, 2000);
      } else {
        // Some or all failed
        let msg = '';
        if (successCount > 0) {
          msg += `Successfully created ${successCount} listings. `;
        }
        msg += `Failed to create ${failedItems.length} listings: ${failures.map(f => f.name).join(', ')}. Please check fields and try again.`;
        setError(msg);
      }

    } catch (error: any) {
      console.error('Critical failure in batch process:', error);
      setError(t('createListing.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/marketplace')}
          className="text-blue-600 hover:text-blue-700 mb-4 flex items-center"
        >
          ← {t('createListing.title.back')}
        </button>
        <h1 className="text-4xl font-bold mb-2">
          {listingType === 'WTS' ? t('createListing.title.wts') : t('createListing.title.wtb')}
        </h1>
        <p className="text-gray-600">
          {listingType === 'WTS'
            ? t('createListing.subtitle.wts')
            : t('createListing.subtitle.wtb')}
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {error === (t('marketplaceListing.approvalRequiredDesc') || "Account Approval Required") ? (
        <div className="text-center py-8">
          <button onClick={() => navigate('/marketplace')} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
            {t('marketplaceListing.back')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Step 1: Add Cards */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">
              1. {t('createListing.step2.titleSingle').replace(/^\d+\.\s*/, '')}
            </h2>

            {/* Search Box */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('createListing.step2.searchLabel')}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder={t('createListing.step2.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch())}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {t('createListing.step2.searchButton')}
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-2 border border-gray-200 rounded-lg max-h-96 overflow-y-auto bg-gray-50">
                  {searchResults.map((card) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => addItem(card)}
                      className="w-full p-3 hover:bg-blue-50 flex items-center gap-4 border-b last:border-b-0 text-left transition"
                    >
                      {card.imageUrl ? (
                        <img
                          src={card.imageUrl}
                          alt={card.name}
                          className="w-12 h-12 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">No Img</div>
                      )}
                      <div>
                        <div className="font-medium text-blue-900">{card.name}</div>
                        <div className="text-xs text-gray-500">
                          #{card.cardNumber} • {card.rarity} • {card.type}
                        </div>
                      </div>
                      <div className="ml-auto text-sm text-blue-600 font-semibold px-2">
                        + Add
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Items List */}
            {listingItems.length > 0 ? (
              <div className="space-y-4">
                {listingItems.map((item, index) => (
                  <div key={item.id} className="border border-green-200 bg-green-50/50 rounded-lg p-3 md:p-4 flex items-center gap-2 md:gap-4 animate-fadeIn">
                    {/* Card Info */}
                    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                      <div className="text-xs font-bold text-gray-400 w-4 md:w-6 hidden sm:block">#{index + 1}</div>
                      {item.card.imageUrl ? (
                        <img
                          src={item.card.imageUrl}
                          alt={item.card.name}
                          className="w-10 h-10 md:w-14 md:h-14 object-cover rounded shadow-sm border border-gray-200 bg-white flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 md:w-14 md:h-14 bg-gray-200 rounded flex items-center justify-center text-[10px] md:text-xs text-gray-500 flex-shrink-0">Img</div>
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-gray-900 truncate text-sm md:text-base">{item.card.name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          #{item.card.cardNumber} • {item.card.rarity}
                        </div>
                      </div>
                    </div>

                    {/* Inputs */}
                    <div className="flex gap-2 items-center">
                      {/* Price */}
                      <div className="w-20 md:w-32">
                        <label className="block text-[10px] md:text-xs font-medium text-gray-600 mb-0.5 md:mb-1">
                          {t('createListing.step4.price')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="Opt"
                          value={item.price}
                          onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                          className="w-full px-1.5 py-1 md:px-2 md:py-1.5 text-xs md:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>

                      {/* Quantity */}
                      <div className="w-14 md:w-20">
                        <label className="block text-[10px] md:text-xs font-medium text-gray-600 mb-0.5 md:mb-1">
                          {t('createListing.step4.quantity').substring(0, 3)} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                          className="w-full px-1.5 py-1 md:px-2 md:py-1.5 text-xs md:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="text-red-400 hover:text-red-600 p-1 md:p-2 mt-3 md:mt-4 self-center"
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                {t('createListing.step2.noItems')}
              </div>
            )}

            {listingItems.length > 0 && (
              <div className="mt-4 text-right text-sm text-gray-500">
                {t('createListing.step2.totalListings')}: <strong>{listingItems.length}</strong>
              </div>
            )}

            <div className="mt-4 p-3 bg-blue-50 text-blue-800 text-sm rounded-lg flex items-start gap-2">
              <span className="text-lg">ℹ️</span>
              <p>
                {t('createListing.step4.priceHelper')}
              </p>
            </div>

          </div>

          {/* Step 2: Global Details */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">
              2. {t('createListing.step4.title').replace(/^\d+\.\s*/, '')} <span className="text-base font-normal text-gray-500">{t('createListing.step4.appliedToAll')}</span>
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              {/* External Link */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('createListing.step4.externalLink')}
                </label>
                <input
                  type="url"
                  placeholder="https://facebook.com/..."
                  value={externalLink}
                  onChange={(e) => setExternalLink(e.target.value)}
                  className="w-full px-4 py-2 border border-blue-200 bg-blue-50 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('createListing.step4.externalLinkHelper')}
                </p>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('createListing.step4.description')}
                </label>
                <textarea
                  placeholder={t('createListing.step4.descPlaceholder')}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/marketplace')}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-medium"
            >
              {t('createListing.step4.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || listingItems.length === 0}
              className={`flex-1 py-3 rounded-lg transition font-medium disabled:opacity-50 ${listingType === 'WTS'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-green-600 text-white hover:bg-green-700'
                }`}
            >
              {loading
                ? t('createListing.step4.submitting')
                : (listingType === 'WTS'
                  ? t('createListing.step4.submitWts', { count: listingItems.length })
                  : t('createListing.step4.submitWtb', { count: listingItems.length })
                )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default CreateListingPage;
